import React, { useState } from 'react';
import { Card, Button, Tag, Space, Typography, Modal, Descriptions, Empty } from 'antd';
import { EnvironmentOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { ConfigurableLocationSelector } from '@/modules/location/components/ConfigurableLocationSelector';
import type { CustomerCategoryLocationDTO, LocationDTO } from '@/shared/api/model';

const { Text, Title } = Typography;

export interface LocationData {
  locationConfigName: string;
  addressType: string;
  locationSelections: {
    zone?: LocationDTO;
    region?: LocationDTO;
    district?: LocationDTO;
    ward?: LocationDTO;
    street?: LocationDTO;
  };
  houseNumber?: string;
  postalCode?: string;
  gpsCoordinates?: string;
  isPrimary: boolean;
  displayOrder: number;
}

export interface LocationDisplayCardProps {
  locationConfig: CustomerCategoryLocationDTO;
  selectedLocation?: LocationData;
  onLocationChange: (location: LocationData) => void;
  onLocationRemove?: () => void;
  disabled?: boolean;
}

const LocationDisplayCard: React.FC<LocationDisplayCardProps> = ({
  locationConfig,
  selectedLocation,
  onLocationChange,
  onLocationRemove,
  disabled = false,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [tempLocationData, setTempLocationData] = useState<any>(null);

  const handleOpenModal = () => {
    setIsModalVisible(true);
    setTempLocationData(null);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setTempLocationData(null);
  };

  const handleLocationSelected = (data: any) => {
    setTempLocationData(data);
  };

  const handleSaveLocation = () => {
    if (tempLocationData && tempLocationData.locationSelections) {
      const locationData: LocationData = {
        locationConfigName: locationConfig.locationConfigName!,
        addressType: locationConfig.addressType!,
        locationSelections: tempLocationData.locationSelections,
        houseNumber: tempLocationData.houseNumber,
        postalCode: tempLocationData.postalCode,
        gpsCoordinates: tempLocationData.gpsCoordinates 
          ? `${tempLocationData.gpsCoordinates.latitude},${tempLocationData.gpsCoordinates.longitude}`
          : undefined,
        isPrimary: locationConfig.isPrimary || false, // Use config value
        displayOrder: locationConfig.displayOrder || 0,
      };
      
      onLocationChange(locationData);
      handleCloseModal();
    }
  };

  const handleRemoveLocation = () => {
    if (onLocationRemove) {
      onLocationRemove();
    }
  };

  // Build location hierarchy display
  const buildLocationPath = (): string => {
    if (!selectedLocation || !selectedLocation.locationSelections) {
      return 'Not selected';
    }

    const parts: string[] = [];
    const { zone, region, district, ward, street } = selectedLocation.locationSelections;

    if (zone) parts.push(zone.name!);
    if (region) parts.push(region.name!);
    if (district) parts.push(district.name!);
    if (ward) parts.push(ward.name!);
    if (street) parts.push(street.name!);

    return parts.length > 0 ? parts.join(' → ') : 'Not selected';
  };

  const locationPath = buildLocationPath();
  const hasLocation = selectedLocation && selectedLocation.locationSelections && 
    Object.keys(selectedLocation.locationSelections).length > 0;

  return (
    <>
      <Card
        size="small"
        title={
          <Space>
            <EnvironmentOutlined />
            <span>{locationConfig.customLabel || `${locationConfig.addressType} Address`}</span>
            {locationConfig.isRequired && <Tag color="red">Required</Tag>}
            {locationConfig.isPrimary && <Tag color="blue">Primary</Tag>}
          </Space>
        }
        extra={
          <Space>
            {hasLocation && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={handleOpenModal}
                disabled={disabled}
              >
                Change
              </Button>
            )}
            {!locationConfig.isRequired && hasLocation && onLocationRemove && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={handleRemoveLocation}
                disabled={disabled}
              >
                Remove
              </Button>
            )}
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        {hasLocation ? (
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Location">
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <Text strong>{locationPath}</Text>
              </Space>
            </Descriptions.Item>
            {selectedLocation.houseNumber && (
              <Descriptions.Item label="House Number">
                {selectedLocation.houseNumber}
              </Descriptions.Item>
            )}
            {selectedLocation.postalCode && (
              <Descriptions.Item label="Postal Code">
                {selectedLocation.postalCode}
              </Descriptions.Item>
            )}
            {selectedLocation.gpsCoordinates && (
              <Descriptions.Item label="GPS Coordinates">
                {selectedLocation.gpsCoordinates}
              </Descriptions.Item>
            )}
          </Descriptions>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" size="small">
                <Text type="secondary">No location selected</Text>
                <Button
                  type="primary"
                  size="small"
                  icon={<EnvironmentOutlined />}
                  onClick={handleOpenModal}
                  disabled={disabled}
                >
                  Select Location
                </Button>
              </Space>
            }
          />
        )}

        {locationConfig.description && (
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
            {locationConfig.description}
          </Text>
        )}
      </Card>

      <Modal
        title={
          <Space>
            <EnvironmentOutlined />
            <span>{locationConfig.customLabel || `${locationConfig.addressType} Address`}</span>
          </Space>
        }
        open={isModalVisible}
        onCancel={handleCloseModal}
        onOk={handleSaveLocation}
        okText="Save Location"
        cancelText="Cancel"
        width={800}
        okButtonProps={{
          disabled: !tempLocationData || !tempLocationData.locationSelections || 
            Object.keys(tempLocationData.locationSelections).length === 0
        }}
      >
        <ConfigurableLocationSelector
          title={locationConfig.customLabel || `${locationConfig.addressType} Address`}
          entityType="CUSTOMER"
          addressType={locationConfig.addressType!}
          onLocationSelected={handleLocationSelected}
          disabled={false}
          initialValues={selectedLocation ? {
            locationSelections: selectedLocation.locationSelections,
            houseNumber: selectedLocation.houseNumber,
            postalCode: selectedLocation.postalCode,
            isPrimary: selectedLocation.isPrimary,
          } : undefined}
        />
      </Modal>
    </>
  );
};

export default LocationDisplayCard;
